import { isInteger } from 'lodash';
import jsCookie from 'js-cookie';
import { VISION_INSURANCE_SPECIALTIES, DENTAL_INSURANCE_SPECIALTIES } from '@zocdoc/components/src/constants';
import { USER_SPECIALTY_INSURANCE_TYPES } from '../constants';
import { CARRIER } from '@zocdoc/components/src/InsuranceForm/constants';

export const chooseInsuranceLater = {
    carrierId: CARRIER.ILL_CHOOSE_LATER,
    planId: '-1',
};

export const insuranceNotChosen = {
    carrierId: CARRIER.CARRIER_NOT_CHOSEN,
    planId: '-1',
};

export const getInsuranceFromSpecialtyIds = ({
    cookies,
    insuranceFromBrandConfig = {},
    insuranceQueryParameters = {},
    specialtyIds,
}) => {
    // insurance fallback priority url > cookie > default in theme
    // however, some directory hospitals (e.g. GHP) only allow one insurance carrier.
    // and the carrier id is set in the theme as a default carrier.
    // Thus, if given insurance carrier id does not match to the default,
    // We should return id '-1', 'I will choose my insurance later'
    // However, if neither user insurance nor brand insurance are passed in,
    // default to 'insurance not chosen'

    const userInsurance = insuranceQueryParameters.carrierId
        ? insuranceQueryParameters
        : getInsuranceFromCookie(specialtyIds, cookies);

    const insuranceToUse = getInsuranceToUse(userInsurance, insuranceFromBrandConfig);

    if (insuranceToUse === chooseInsuranceLater) {
        return insuranceToUse;
    }

    if (!isInteger(parseInt(insuranceToUse.carrierId, 10))) {
        return insuranceNotChosen;
    }

    if (!isInteger(parseInt(insuranceToUse.planId, 10))) {
        return {
            carrierId: insuranceToUse.carrierId,
            planId: insuranceNotChosen.planId,
        };
    }

    const isValidInsurance = isInteger(parseInt(insuranceToUse.carrierId, 10))
        && isInteger(parseInt(insuranceToUse.planId, 10));

    return isValidInsurance
        ? insuranceToUse
        : insuranceNotChosen;
};

const getInsuranceToUse = (userInsurance, insuranceFromBrandConfig) => {
    const brandConfigDoesNotMatchUserInsurance = insuranceFromBrandConfig.carrierId
    && userInsurance.carrierId
    && userInsurance.carrierId !== insuranceFromBrandConfig.carrierId;

    if (brandConfigDoesNotMatchUserInsurance) {
        return chooseInsuranceLater;
    }
    if (userInsurance.carrierId) {
        return userInsurance;
    }
    if (insuranceFromBrandConfig.carrierId) {
        return insuranceFromBrandConfig;
    }
    return insuranceNotChosen;
};

const getInsuranceFromCookie = (specialtyIds, cookies) => {
    if (specialtyIds.some(isVision)) {
        return {
            carrierId: cookies.vCarrierId,
            planId: cookies.vPlanId,
        };
    } else if (specialtyIds.some(isDental)) {
        return {
            carrierId: cookies.dCarrierId,
            planId: cookies.dPlanId,
        };
    } else {
        return {
            carrierId: cookies.hCarrierId,
            planId: cookies.hPlanId,
        };
    }
};

export const getCookiedInsuranceFromUserSpecialtyType = specialtyType => {
    // This function returns a carrier and plan Id from a user specialty type
    // If the corresponding cookie doesn't exist, it returns null
    let carrierId;
    let planId;
    switch (specialtyType) {
        case USER_SPECIALTY_INSURANCE_TYPES.DENTAL:
            carrierId = jsCookie.get('dCarrierId');
            planId = jsCookie.get('dPlanId');
            if (isValidCookiedInsurance(carrierId, planId)) {
                return {
                    carrierId,
                    planId,
                };
            }
            break;
        case USER_SPECIALTY_INSURANCE_TYPES.MEDICAL:
            carrierId = jsCookie.get('hCarrierId');
            planId = jsCookie.get('hPlanId');
            if (isValidCookiedInsurance(carrierId, planId)) {
                return {
                    carrierId,
                    planId,
                };
            }
            break;
        case USER_SPECIALTY_INSURANCE_TYPES.VISION:
            carrierId = jsCookie.get('vCarrierId');
            planId = jsCookie.get('vPlanId');
            if (isValidCookiedInsurance(carrierId, planId)) {
                return {
                    carrierId,
                    planId,
                };
            }
            break;
        default:
            return null;
    }
    return null;
};

const isValidCookiedInsurance = (planId, carrierId) =>
    carrierId && planId && carrierId !== '-1' && planId !== '-1';

export const getUserSpecialtyTypeFromSpecialtyId = specialtyId => {
    if (isDental(specialtyId)) {
        return USER_SPECIALTY_INSURANCE_TYPES.DENTAL;
    }
    if (isVision(specialtyId)) {
        return USER_SPECIALTY_INSURANCE_TYPES.VISION;
    }
    return USER_SPECIALTY_INSURANCE_TYPES.MEDICAL;
};

const isDental = specialtyId => {
    return DENTAL_INSURANCE_SPECIALTIES.has(specialtyId);
};

const isVision = specialtyId => {
    return VISION_INSURANCE_SPECIALTIES.has(specialtyId);
};
